import { useState } from 'react'
import { useSubscribeNewsletter } from '@/lib/api/hooks/newsletter'
import { CheckCircle, AlertCircle } from 'lucide-react'

interface NewsletterProps {
  className?: string
}

const Newsletter = ({ className }: NewsletterProps) => {
  const [email, setEmail] = useState('')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const subscribeMutation = useSubscribeNewsletter()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      const response = await subscribeMutation.mutateAsync({ email })

      if (response.success) {
        setSuccessMessage(response.message || 'Successfully subscribed to our newsletter!')
        setEmail('')
      } else {
        setErrorMessage(response.message || 'Failed to subscribe. Please try again.')
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to subscribe. Please try again.'
      )
    }
  }

  return (
    <section className={`w-full ${className || ''}`}>
      <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-normal font-pt-serif text-gray-900 leading-tight sm:leading-loose">
            JOIN OUR NEWSLETTER
          </h2>
          <p className="text-sm sm:text-base lg:text-lg font-normal font-pt-serif text-gray-600 leading-relaxed max-w-lg">
            Get the latest updates, insights, and exclusive content delivered
            straight to your inbox.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <label
            htmlFor="newsletter-email"
            className="block text-sm sm:text-base font-normal font-satoshi text-gray-900"
          >
            Email Address
          </label>

          <div className="flex flex-col sm:flex-row sm:max-w-lg w-full gap-2 sm:gap-0">
            <input
              type="email"
              id="newsletter-email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setSuccessMessage(null)
                setErrorMessage(null)
              }}
              placeholder="Johndoe@email.com"
              className=" h-12 px-4 bg-gray-50 text-base font-normal font-satoshi text-gray-900 placeholder-gray-600  focus:outline-none  focus:border-transparent"
              required
              disabled={subscribeMutation.isPending}
            />
            <button
              type="submit"
              disabled={subscribeMutation.isPending}
              className="h-12 px-4 bg-wine-100 hover:bg-wine-200 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm sm:text-base font-bold font-satoshi  transition-colors duration-200 whitespace-nowrap"
            >
              {subscribeMutation.isPending ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
            </button>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-medium font-satoshi">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-medium font-satoshi">{errorMessage}</p>
            </div>
          )}
        </form>
      </div>
    </section>
  )
}

export default Newsletter